'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import styles from './custom.module.css';

const steps = [
  {
    num: '01',
    title: 'Discovery Call',
    desc: 'We start by understanding your business inside and out — your workflows, goals, customer interactions, and specific requirements. No templates, no assumptions.',
    illus: (
      <div className={styles.illusDiscovery}>
        <div className={styles.illusDiscoveryCard}>
          <div className={styles.illusDiscoveryTitle}>Discovery Session</div>
          <div className={styles.illusDiscoveryRow}><span>Goals</span><span>Defined ✓</span></div>
          <div className={styles.illusDiscoveryRow}><span>Workflows</span><span>Mapped ✓</span></div>
          <div className={styles.illusDiscoveryRow}><span>Integrations</span><span>Identified ✓</span></div>
          <div className={styles.illusDiscoveryRow}><span>Requirements</span><span>Documented ✓</span></div>
        </div>
      </div>
    ),
  },
  {
    num: '02',
    title: 'Design & Build',
    desc: 'Our team designs and builds your voice agent from the ground up — custom voice, custom logic, custom integrations. Every detail crafted to your exact specifications.',
    illus: (
      <div className={styles.illusPanel}>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Agent Name</span>
          <span className={styles.illusPanelValue}>Your Choice</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Voice</span>
          <span className={styles.illusPanelValue}>Custom Built</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Workflow</span>
          <span className={styles.illusPanelValue}>Fully Custom</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Integrations</span>
          <span className={`${styles.illusPanelValue} ${styles.illusPanelOn}`}>Connected ✓</span>
        </div>
      </div>
    ),
  },
  {
    num: '03',
    title: 'Test & Refine',
    desc: 'You test the agent in real scenarios. We listen, adjust, and refine until every interaction feels exactly right. No compromises — we iterate until you\'re fully satisfied.',
    illus: (
      <div className={styles.illusTestCard}>
        <div className={styles.illusTestTitle}>Refinement Cycle</div>
        <div className={styles.illusTestRow}><span>Test Round</span><span>3 of 3</span></div>
        <div className={styles.illusTestRow}><span>Adjustments</span><span>Applied ✓</span></div>
        <div className={styles.illusTestRow}><span>Approval</span><span className={styles.illusTestGreen}>Confirmed ✓</span></div>
        <div className={styles.illusTestBadge}>✓ Ready to Deploy</div>
      </div>
    ),
  },
  {
    num: '04',
    title: 'Go Live & Ongoing Support',
    desc: 'Your custom agent goes live with a dedicated support team behind it. As your business evolves, we evolve your agent — updates, new features, continuous improvement.',
    illus: (
      <div className={styles.illusLive}>
        <div className={styles.illusLiveBadge}>
          <span className={styles.illusLiveDot} />
          LIVE
        </div>
        <ul className={styles.illusLiveList}>
          <li>Dedicated support team</li>
          <li>Continuous improvements</li>
          <li>Scales with your business</li>
        </ul>
      </div>
    ),
  },
];

export default function CustomAgentPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const goTo = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, steps.length - 1));
    setCurrentSlide(clamped);
    const vp = viewportRef.current;
    if (!vp) return;
    const cards = vp.querySelectorAll('[data-card]');
    cards[clamped]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };

  return (
    <div className={styles.page}>

      {/* ── Navigation ── */}
      <NavBar />

      {/* ── Hero ── */}
      <section className={styles.hero}>
        <Image
          src="/images/meeting.jpg"
          alt="Custom Agent background"
          fill
          priority
          className={styles.heroImage}
        />
        <div className={styles.heroOverlay} />
        <div className={styles.heroLayout}>
          <div className={styles.heroContent}>
            <span className={styles.heroTag}>Custom AI Voice Agent</span>
            <h1 className={styles.heroTitle}>Built for You. Built Around You.</h1>
            <p className={styles.heroSubtitle}>
              Every business is different. We design and build a voice agent from the ground up — tailored to your exact workflow, brand, and requirements. No templates, no compromises.
            </p>
            <div className={styles.heroBtns}>
              <Link href="/register?industry=custom" className={styles.heroBtnPrimary}>Request a Custom Agent</Link>
              <Link href="#how-it-works" className={styles.heroBtnSecondary}>See How It Works</Link>
            </div>
          </div>

          {/* ── Book Demo Widget ── */}
          <div className={styles.audioWidget}>
            <div className={styles.audioWidgetHeader}>
              <div className={styles.audioAvatar}>AI</div>
              <div>
                <div className={styles.audioAgentName}>Your Agent</div>
                <div className={styles.audioAgentStatus}>
                  <span className={styles.audioStatusDotActive} />
                  Tailored to your business
                </div>
              </div>
            </div>
            <div className={styles.audioWaveform}>
              {[...Array(16)].map((_, i) => (
                <span key={i} className={`${styles.audioWaveBar} ${styles.audioWaveBarActive}`} style={{ animationDelay: `${i * 0.07}s` }} />
              ))}
            </div>
            <Link href="/contact" className={styles.audioPlayBtn} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              Book a Live Demo
            </Link>
            <p className={styles.audioLabel}>We&apos;ll build your agent together</p>
          </div>

        </div>
      </section>

      {/* ── Features ── */}
      <section className={styles.featuresSection}>
        <div className={styles.featuresInner}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>FULLY TAILORED · BUILT FROM SCRATCH · YOUR VISION</span>
            <h2>Your Business Is Unique.<br />Your Voice Agent Should Be Too.</h2>
            <p>We don&apos;t fit your business into a template — we build around it. Every workflow, every integration, every interaction designed specifically for you.</p>
          </div>
          <div className={styles.featuresGrid}>

            {/* Card 1 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusVoiceCard}>
                  <div className={styles.illusVoiceTitle}>Custom Voice Profile</div>
                  <div className={styles.illusVoiceRow}><span>Name</span><span>Your Choice</span></div>
                  <div className={styles.illusVoiceRow}><span>Tone</span><span>Your Brand</span></div>
                  <div className={styles.illusVoiceRow}><span>Personality</span><span>Fully Custom</span></div>
                  <div className={styles.illusVoiceBadge}>✓ Unique to Your Business</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Fully Custom Voice & Personality</h3>
                <p className={styles.fcSubtitle}>Your agent gets a unique name, voice, tone, and personality — perfectly aligned with your brand.</p>
              </div>
            </div>

            {/* Card 2 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusWorkflowCard}>
                  <div className={styles.illusWorkflowTitle}>Custom Workflow</div>
                  <div className={styles.illusWorkflowRow}><span>Step 1</span><span>Verify Caller</span></div>
                  <div className={styles.illusWorkflowRow}><span>Step 2</span><span>Route to Dept.</span></div>
                  <div className={styles.illusWorkflowRow}><span>Step 3</span><span>Log to CRM</span></div>
                  <div className={styles.illusWorkflowRow}><span>Step 4</span><span>Send Summary</span></div>
                  <div className={styles.illusWorkflowBadge}>✓ Built to Your Spec</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Custom Workflow Integration</h3>
                <p className={styles.fcSubtitle}>We map and automate your exact call workflows — no rigid scripts, no workarounds.</p>
              </div>
            </div>

            {/* Card 3 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusIntCard}>
                  <div className={styles.illusIntTitle}>System Integrations</div>
                  <div className={styles.illusIntRow}><span>CRM</span><span>Salesforce · HubSpot</span></div>
                  <div className={styles.illusIntRow}><span>EHR</span><span>Epic · Cerner</span></div>
                  <div className={styles.illusIntRow}><span>PMS</span><span>Custom API</span></div>
                  <div className={styles.illusIntBadge}>✓ Fully Connected</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>EHR, CRM & System Integration</h3>
                <p className={styles.fcSubtitle}>We connect your agent to any system — CRM, EHR, PMS, or custom APIs — so data flows seamlessly.</p>
              </div>
            </div>

            {/* Card 4 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusScriptCard}>
                  <div className={styles.illusScriptTitle}>Call Logic & Scripting</div>
                  <div className={styles.illusScriptRow}><span>If caller says...</span><span>Route to billing</span></div>
                  <div className={styles.illusScriptRow}><span>If after hours...</span><span>Take message</span></div>
                  <div className={styles.illusScriptRow}><span>If VIP caller...</span><span>Priority queue</span></div>
                  <div className={styles.illusScriptBadge}>✓ Logic Defined</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Custom Call Scripting & Logic</h3>
                <p className={styles.fcSubtitle}>Define exactly how your agent handles every scenario — conditional logic built to your rules.</p>
              </div>
            </div>

            {/* Card 5 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusTeamCard}>
                  <div className={styles.illusTeamTitle}>Dedicated Build Team</div>
                  <div className={styles.illusTeamRow}><span>Project Manager</span><span>Assigned ✓</span></div>
                  <div className={styles.illusTeamRow}><span>AI Engineer</span><span>Assigned ✓</span></div>
                  <div className={styles.illusTeamRow}><span>QA Specialist</span><span>Assigned ✓</span></div>
                  <div className={styles.illusTeamBadge}>✓ Your Team is Ready</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Dedicated Build & Onboarding Team</h3>
                <p className={styles.fcSubtitle}>A dedicated team guides you from discovery through go-live — you&apos;re never handed a manual and left alone.</p>
              </div>
            </div>

            {/* Card 6 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusSupportCard}>
                  <div className={styles.illusSupportTitle}>Ongoing Support</div>
                  <div className={styles.illusSupportRow}><span>Updates</span><span>As needed</span></div>
                  <div className={styles.illusSupportRow}><span>New Features</span><span>On request</span></div>
                  <div className={styles.illusSupportRow}><span>Performance</span><span>Monitored 24/7</span></div>
                  <div className={styles.illusSupportBadge}>✓ Always Improving</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Ongoing Support & Iterations</h3>
                <p className={styles.fcSubtitle}>As your business evolves, your agent evolves too — we update, refine, and improve continuously.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className={styles.howItWorksSection}>
        <div className={styles.howItWorksHeader}>
          <div>
            <h2 className={styles.howTitle}>
              <em className={styles.howTitleItalic}>How</em> It Works.
            </h2>
            <p className={styles.howSubtitle}>From discovery to a live custom agent — a process built around you</p>
          </div>
          <div className={styles.sliderArrows}>
            <button
              className={styles.arrowBtn}
              onClick={() => goTo(currentSlide - 1)}
              disabled={currentSlide === 0}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className={styles.arrowBtn}
              onClick={() => goTo(currentSlide + 1)}
              disabled={currentSlide === steps.length - 1}
              aria-label="Next"
            >
              ›
            </button>
          </div>
        </div>

        <div className={styles.sliderViewport} ref={viewportRef}>
          {steps.map((step, i) => (
            <div
              key={i}
              data-card
              className={`${styles.slideCard} ${styles[`slideCard${i + 1}`]}`}
            >
              <span className={styles.slideNum}>{step.num}</span>
              <div className={styles.slideIllus}>{step.illus}</div>
              <div className={styles.slideText}>
                <h3 className={styles.slideTitle}>{step.title}</h3>
                <p className={styles.slideDesc}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaSection}>
        <Image
          src="/images/meeting1.png"
          alt="CTA background"
          fill
          priority
          className={styles.ctaImage}
        />
        <div className={styles.ctaOverlay} />
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Ready to Build Your Custom Voice Agent?</h2>
          <p className={styles.ctaSubtitle}>Tell us about your business and we&apos;ll design a voice agent built exactly around your needs</p>
          <Link href="/register?industry=custom" className={styles.ctaBtn}>Request a Custom Agent</Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <Footer />

    </div>
  );
}
